import { create } from 'zustand'

type PaletteLayout = 'modal' | 'sheet'

interface LookupPaletteState {
  isOpen: boolean
  layout: PaletteLayout
  /**
   * File to auto-run OCR on when the palette opens.
   * Set by openWithOcrFile (e.g. from the Home "+" sheet photo path).
   * Cleared by clearOcrFile after the palette mounts or on close.
   */
  initialOcrFile: File | null
  /**
   * Text to prefill the palette's input with when it opens.
   *
   * The one caller today is in-deck search's "search everywhere" exit (brief
   * §15): the two searches are deliberately different tools, so the boundary
   * between them has to be crossable without retyping — a reader who searched
   * this deck and missed should reach the global search already carrying their
   * word. Cleared alongside the OCR file when the palette closes.
   */
  initialQuery: string | null
  /** Open palette as a bottom sheet (mobile UX) */
  openAsSheet: () => void
  /** Open palette as a centered modal (desktop / Cmd+K UX) */
  openAsModal: () => void
  /** Open as a sheet and queue an OCR file to auto-process on open. */
  openWithOcrFile: (file: File) => void
  /** Open as a sheet with the input prefilled — the in-deck → global handoff. */
  openWithQuery: (query: string) => void
  close: () => void
  clearOcrFile: () => void
}

export const useLookupPaletteStore = create<LookupPaletteState>((set) => ({
  isOpen: false,
  layout: 'modal',
  initialOcrFile: null,
  initialQuery: null,
  openAsSheet: () => set({ isOpen: true, layout: 'sheet' }),
  openAsModal: () => set({ isOpen: true, layout: 'modal' }),
  openWithOcrFile: (file: File) => set({ isOpen: true, layout: 'sheet', initialOcrFile: file }),
  openWithQuery: (query: string) => set({ isOpen: true, layout: 'sheet', initialQuery: query }),
  close: () => set({ isOpen: false }),
  clearOcrFile: () => set({ initialOcrFile: null, initialQuery: null }),
}))
